import { MenuItemDivider } from '@components/EmailSignaturev2/EmailSignatureSideEditor/EmailSignatureEditorMenu';
// components
import {
  SideEditorHelpItemsContainer,
  SideEditorMenu,
  SideEditorMenuItem,
} from '@components/SideEditor/SideEditor';
import { useSideEditorContext } from '@components/SideEditor/utils';
import {
  type IconDefinition,
  faImages,
  faPalette,
  faQuestionCircle,
  faShareAlt,
  faStream,
  faUser,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useOnce from '@hooks/use-once';
import { analytics } from '@modules/analytics';
import { generateEntityId } from '@modules/helpers';
import { type FC, useEffect, useState } from 'react';

import styles from './OnboardingEditorMenu.module.scss';
import type { AnalyticsSignatureFeatureTabType } from '.cache/__types__';

const MenuItem: FC<
  React.PropsWithChildren<{
    title: string;
    itemIcon: IconDefinition;
    index: number;
  }>
> = ({ title, itemIcon, index }) => {
  const { activeTab } = useSideEditorContext();
  const [visited, setVisited] = useState(false);

  useOnce(
    (preventSecondRun) => {
      if (activeTab === index) {
        analytics.track({
          name: 'onboarding_signature_featuretab_clicked',
          properties: {
            feature_tab_type: title.toLowerCase() as AnalyticsSignatureFeatureTabType,
          },
        });
        setVisited(true);
        preventSecondRun();
      }
    },
    [activeTab, index],
  );

  return (
    <SideEditorMenuItem
      icon={
        <div className={styles['icon-container']}>
          <FontAwesomeIcon icon={itemIcon} size="2x" />
          {activeTab !== index && !visited && <div className={styles['beacon']}></div>}
        </div>
      }
      index={index}
      title={title}
    />
  );
};

interface OnboardingEditorMenuProps {
  emailSignatureHasBeenLoaded: boolean;
  isTeamEmailSignature: boolean;
  onHelpClick: () => void;
}

const OnboardingEditorMenu: FC<React.PropsWithChildren<OnboardingEditorMenuProps>> = ({
  emailSignatureHasBeenLoaded,
  isTeamEmailSignature,
  onHelpClick,
}) => {
  const [sideEditorKey, setSideEditorKey] = useState<string>(generateEntityId());

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    // this is a fix for reach ui bug where the indices of the menu item is messed up when
    // you remove or add extra tab
    setSideEditorKey(generateEntityId());
  }, [isTeamEmailSignature, emailSignatureHasBeenLoaded]);

  return (
    <SideEditorMenu key={sideEditorKey}>
      <MenuItem index={0} title={'Details'} itemIcon={faUser} />
      <MenuItem index={1} title={'Images'} itemIcon={faImages} />
      <MenuItem index={2} title={'Links'} itemIcon={faShareAlt} />
      <MenuItem index={3} title={'Design'} itemIcon={faPalette} />
      <MenuItemDivider />
      <MenuItem index={4} title={'Banners'} itemIcon={faStream} />
      <SideEditorHelpItemsContainer>
        <div className={styles['help-button-container']}>
          <button className={styles['help-button']} onClick={onHelpClick}>
            <FontAwesomeIcon icon={faQuestionCircle} />
          </button>
        </div>
      </SideEditorHelpItemsContainer>
    </SideEditorMenu>
  );
};

export default OnboardingEditorMenu;
